import type { Lesson } from '../types';
import { f, findSample } from './helpers';

const WEIGHT = 9.81;

/** The first moment in the thrust run where thrust is below weight while still moving up. */
const coastingUp = (ctx: Parameters<typeof findSample>[0]) =>
  findSample(ctx, 'm0-thrust', (s) => s.T < WEIGHT - 0.2 && s.v > 0.05) ??
  findSample(ctx, 'm0-thrust', (s) => s.T < WEIGHT - 0.2)!;

export const m0: Lesson = {
  id: 'm0',
  number: 0,
  title: 'Meet the vehicle',
  summary: 'In this module, you will take the controls to a drone and fly it manually. The goal is to build an intuitive feel for how thrust, weight, and drag influence flight.',
  objectives: [
    'Name the parts of the altitude-control loop.',
    'Read altitude, velocity and thrust plots with their units.',
    'Explain why moving upward does not mean accelerating upward.',
  ],
  controls: ['manual', 'acceleration'],
  controllerKinds: ['manual'],
  sections: [
    {
      id: 'm0-loop',
      title: 'The pieces of a control loop',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'When building an autonomous flight controller, an aerospace engineer’s very first task is mapping out the system architecture: what physical machine is being controlled, what actuators apply forces, what sensors report its state, and what target it must track. Breaking down a complex flying machine into standard control-loop blocks makes it possible to model, simulate, and automate.',
        },
        {
          type: 'p',
          text: 'In our training setup, the vehicle is a 1 kg multirotor constrained to a vertical guide rod. This keeps the aircraft level and restricts motion to straight up and down, letting us master altitude control without the added complication of 3D pitching or rolling.',
        },
        {
          type: 'table',
          caption: 'Parts of the altitude-control loop',
          headers: ['Part', 'In our vehicle'],
          rows: [
            ['{{plant}}', 'The physical system being controlled: the 1 kg vehicle and anything attached to it.'],
            ['{{actuator}}', 'The hardware that produces physical forces: spinning rotors pushing up with thrust {{T}} between 0 and {{Tmax}} = 25 N.'],
            ['{{sensor}}', 'The instrument that measures what the vehicle is doing: an altimeter reporting altitude.'],
            ['{{reference}}', 'The target altitude you want the aircraft to reach and hold, shown as a dashed line.'],
            ['{{disturbance}}', 'Unplanned outside forces acting on the vehicle, such as a wind gust or payload load.'],
            ['{{error}}', 'How far the vehicle is from its target: target minus measured altitude ($e = r - y$).'],
          ],
        },
        {
          type: 'p',
          text: 'In this introductory module, no automated flight code is running yet. You are the controller! You will observe the vehicle’s motion and choose motor thrust by hand to build an intuitive feel for flight dynamics.',
        },
      ],
    },
    {
      id: 'm0-forces',
      title: 'Thrust versus weight',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'For any vertical takeoff aircraft, the most fundamental flight regime is steady hover. If a flight controller commands even slightly too little thrust, the aircraft drops; if it commands too much, it climbs indefinitely. To maintain a stationary hover, the flight computer must calculate and balance the competing forces acting on the craft.',
        },
        {
          type: 'p',
          text: 'Along the vertical axis, two primary forces determine what happens:',
        },
        {
          type: 'list',
          items: [
            '**Upward thrust** ({{T}}): The spinning rotors push air downward, generating an upward aerodynamic reaction force that lifts the craft.',
            '**Downward weight** ($m\\,g$): Gravity pulls the vehicle downward with a force equal to mass times gravitational acceleration ($1\\text{ kg} \\times 9.81\\text{ m/s}^2 = 9.81\\text{ N}$).',
          ],
        },
        {
          type: 'math',
          tex: '\\term{T}{T_{\\text{hover}}} = \\term{m}{m}\\,\\term{g}{g} = 1\\,\\text{kg} \\times 9.81\\,\\tfrac{\\text{m}}{\\text{s}^2} = 9.81\\,\\text{N}',
          aria: 'Hover thrust equals mass m times gravity g: 1 kilogram times 9.81 metres per second squared, which is 9.81 newtons.',
        },
        {
          type: 'p',
          text: 'When rotor thrust exactly matches weight ($T = 9.81\\text{ N}$), the net vertical force is zero. According to Newton’s second law ($F = m\\,a$), zero net force produces zero acceleration, allowing the vehicle to hover steadily in place.',
        },
        {
          type: 'callout',
          tone: 'key',
          title: 'Crucial distinction: Velocity is not acceleration',
          text: 'Moving upward (positive velocity $v > 0$) does **not** mean upward net force or upward acceleration! If a drone is climbing upward at 1 m/s and you suddenly drop thrust below 9.81 N, gravity exceeds thrust: the net force points downward, so acceleration is negative ($a < 0$). The drone decelerates while still coasting upward, exactly like a ball tossed into the air. A flight controller must track both velocity and acceleration to predict where the vehicle is heading.',
        },
      ],
    },
    {
      id: 'm0-predict',
      title: 'Predict',
      phase: 'predict',
      blocks: [
        {
          type: 'p',
          text: 'Before running any simulation or flight code, control engineers build mental models of vehicle dynamics. Test your physical intuition with this prediction.',
        },
        {
          type: 'predict',
          id: 'm0-p1',
          prompt: 'The vehicle is hovering at 9.81 N. You raise thrust to 11 N and hold it. What happens to its velocity?',
          options: [
            { id: 'a', text: 'It jumps to a fixed upward speed immediately.' },
            { id: 'b', text: 'It increases gradually, levelling off as {{air-drag}} grows.' },
            { id: 'c', text: 'It stays zero. 11 N is barely more than the weight.' },
          ],
          reveal:
            'Extra thrust creates an upward net force ($11 - 9.81 = +1.19\\text{ N}$), which causes upward acceleration. Acceleration changes velocity gradually, not instantly. As upward speed increases, aerodynamic drag (damping $-b\\,v$) grows until it balances the extra 1.19 N, at which point acceleration returns to zero and velocity levels off near 1.19 m/s.',
        },
      ],
    },
    {
      id: 'm0-lab',
      title: 'Fly by hand',
      phase: 'experiment',
      blocks: [
        {
          type: 'p',
          text: 'Piloting a multirotor by manual throttle gives direct intuition for the challenges of flight control, particularly vehicle momentum, control latency, and how easily human reflexes overcorrect. Experiencing these manual struggles first-hand makes it clear why automated closed-loop flight controllers are necessary.',
        },
        {
          type: 'h',
          text: 'Part 1: Equilibrium Hover at Constant Thrust',
        },
        {
          type: 'p',
          text: '**Purpose**: Verify Newton’s first law in vertical flight. When commanded thrust exactly matches vehicle weight ($T = m g = 9.81\\text{ N}$), the net vertical force is zero, maintaining a steady, stationary hover without drift.',
        },
        {
          type: 'h',
          text: 'Part 2: Vehicle Momentum and Thrust Transients',
        },
        {
          type: 'p',
          text: '**Purpose**: Experience vehicle inertia and control latency. When you add thrust to climb, the drone accelerates upward; cutting thrust does not halt the climb immediately because stored upward momentum keeps the vehicle coasting upward against gravity until net downward force decelerates velocity to zero.',
        },
        { type: 'lab' },
      ],
    },
    {
      id: 'm0-inspect',
      title: 'The plots',
      phase: 'inspect',
      blocks: [
        {
          type: 'p',
          text: 'Flight test engineers rely heavily on telemetry plots to verify vehicle stability and diagnose flight controller behavior. Examining time-series graphs of altitude, velocity, and acceleration reveals physical interactions that are impossible to spot with the naked eye during flight.',
        },
        {
          type: 'p',
          text: 'Open the simulator plots and toggle **Show acceleration** on. In your climb-then-coast run, locate the specific moment when you dropped thrust below 9.81 N while the vehicle was still climbing. Notice how velocity remains positive (rising) even while acceleration flips negative (decelerating downward). (If you reset the simulator, click **Load run** in the controls to restore your saved data).',
        },
      ],
    },
  ],
  lab: {
    id: 'm0-lab',
    version: 1,
    instructions: 'Complete both manual flight runs (15 s each) using the Manual thrust slider: Step 1 tests equilibrium hover; Step 2 tests climb momentum and coasting recovery.',
    steps: [
      {
        id: 'm0-hover',
        title: 'Part 1: Hover at equilibrium ($T = 9.81$ N)',
        presetId: 'free-manual',
        instructions:
          'Load the step, then press Play without adjusting the slider. Watch the altitude stay pinned at 2 m as thrust (9.81 N) perfectly balances vehicle weight ($m g = 9.81\\text{ N}$).',
        permitted: [],
        requires: 'A complete 15 s run.',
      },
      {
        id: 'm0-thrust',
        title: 'Part 2: Climb, then coast',
        presetId: 'free-manual',
        instructions:
          'Load and press Play. Raise thrust to ~11 N for 2–3 s to begin climbing. Then, while actively rising, drop thrust to ~8.5 N and watch the vehicle coast upward against gravity before slowing to a stop; restore thrust to ~9.81 N to recover.',
        permitted: [],
        allowStopped: true,
        check: (r) => {
          const xs = r.trace;
          let raised = false;
          for (let i = 0; i < xs.t.length; i++) {
            if (xs.T[i] > WEIGHT + 0.3) raised = true;
            if (raised && xs.T[i] < WEIGHT - 0.2 && xs.v[i] > 0.05) return null;
          }
          return raised
            ? 'Lower thrust below 9.81 N while the vehicle is still moving up (velocity above zero).'
            : 'Raise thrust above about 10.1 N first so the vehicle climbs.';
        },
        requires: 'Thrust above weight, then below weight while still rising.',
      },
    ],
  },
  gate: {
    id: 'g0',
    version: 1,
    title: 'Check: thrust, weight and acceleration',
    questions: [
      {
        id: 'g0-obs-v',
        version: 1,
        kind: 'choice',
        binds: ['m0-thrust'],
        prompt: (ctx) => {
          const s = coastingUp(ctx);
          return `At the coasting moment from your climb-then-coast run (t = ${f(s.t)} s), is {{v}} still positive on the velocity plot?`;
        },
        table: (ctx) => {
          const s = coastingUp(ctx);
          return {
            caption: 'Sample from your run',
            headers: ['t (s)', 'thrust T (N)', 'weight m g (N)', 'velocity v (m/s)'],
            rows: [[f(s.t), f(s.T), '9.81', f(s.v)]],
          };
        },
        options: [
          { id: 'yes', text: 'Yes, {{v}} is still positive (moving up).' },
          { id: 'no', text: 'No, {{v}} is zero or negative.', feedback: 'Check the velocity column in the table: at this moment the vehicle is still rising.' },
        ],
        correct: (ctx) => (coastingUp(ctx).v > 0 ? 'yes' : 'no'),
        hint: 'Positive velocity means the vehicle is still moving upward.',
        explanation: 'Velocity is still positive: the vehicle has not started falling yet.',
        review: 'm0-inspect',
      },
      {
        id: 'g0-obs-a',
        version: 1,
        kind: 'choice',
        binds: ['m0-thrust'],
        prompt: (ctx) => `At the same moment (t = ${f(coastingUp(ctx).t)} s), what is the sign of {{a}} on the acceleration plot?`,
        options: [
          { id: 'pos', text: 'Positive - accelerating upward.', feedback: 'Thrust is below weight here, so the net force (and acceleration) point down.' },
          { id: 'neg', text: 'Negative - accelerating downward.' },
          { id: 'zero', text: 'Zero - neither speeding up nor slowing down.', feedback: 'Net force is not zero: thrust is below weight.' },
        ],
        correct: () => 'neg',
        hint: 'Acceleration follows the net force, not the direction of motion.',
        explanation: 'Acceleration is negative: the vehicle is slowing its climb even while velocity is still upward.',
        review: 'm0-inspect',
      },
      {
        id: 'g0-obs-T',
        version: 1,
        kind: 'choice',
        binds: ['m0-thrust'],
        prompt: (ctx) => `At the same moment (t = ${f(coastingUp(ctx).t)} s), where is {{T}} compared with 9.81 N on the thrust plot?`,
        options: [
          { id: 'above', text: 'Above 9.81 N (more thrust than weight).', feedback: 'Look at the thrust column: it should be below the hover value.' },
          { id: 'equal', text: 'Equal to 9.81 N (hover).', feedback: 'You lowered thrust below hover while still rising.' },
          { id: 'below', text: 'Below 9.81 N (less thrust than weight).' },
        ],
        correct: () => 'below',
        hint: 'This is the moment you dropped thrust while the vehicle was still rising.',
        explanation: 'Thrust is below weight, which is why acceleration points down.',
        review: 'm0-inspect',
      },
      {
        id: 'g0-q1',
        version: 1,
        kind: 'choice',
        binds: ['m0-thrust'],
        prompt: (ctx) => {
          const s = coastingUp(ctx);
          return `This sample is from your climb-then-coast run at t = ${f(s.t)} s. Compare thrust with the 9.81 N weight. Which way is the vehicle accelerating?`;
        },
        table: (ctx) => {
          const s = coastingUp(ctx);
          return {
            caption: 'Sample from your run',
            headers: ['t (s)', 'thrust T (N)', 'weight m g (N)', 'velocity v (m/s)'],
            rows: [[f(s.t), f(s.T), '9.81', f(s.v)]],
          };
        },
        options: [
          { id: 'up', text: 'Thrust is above weight, so it accelerates upward.', feedback: 'Check the table: is T bigger or smaller than 9.81 N?' },
          { id: 'down', text: 'Thrust is below weight, so it accelerates downward.' },
          {
            id: 'none',
            text: 'It is moving upward, so it must be accelerating upward.',
            feedback: 'You mixed up velocity and acceleration. Acceleration follows the net force, not the direction of motion.',
          },
        ],
        correct: () => 'down',
        hint: 'Net force = thrust − weight − {{air-drag|drag}}. {{air-drag|Drag}} opposes the upward motion too.',
        explanation: 'Less thrust than weight, plus {{air-drag|drag}} while rising, gives a downward net force. Acceleration is downward even while velocity is still upward.',
        review: 'm0-forces',
      },
      {
        id: 'g0-q2',
        version: 1,
        kind: 'choice',
        binds: ['m0-thrust'],
        prompt: (ctx) => `At the same moment (t = ${f(coastingUp(ctx).t)} s), describe velocity and acceleration.`,
        options: [
          { id: 'uu', text: 'Velocity up, acceleration up', feedback: 'Velocity is up, but look at the sign of acceleration on your plot.' },
          { id: 'ud', text: 'Velocity up, acceleration down' },
          { id: 'du', text: 'Velocity down, acceleration up', feedback: 'Check the sign of v in the table.' },
          { id: 'dd', text: 'Velocity down, acceleration down', feedback: 'Check the sign of v in the table.' },
        ],
        correct: (ctx) => (coastingUp(ctx).v > 0 ? 'ud' : 'dd'),
        hint: 'Velocity sign is in the table. Acceleration sign follows the net force.',
        explanation: 'The vehicle is still moving up (v > 0) but slowing down (a < 0).',
        review: 'm0-inspect',
      },
      {
        id: 'g0-q3',
        version: 1,
        kind: 'choice',
        binds: [],
        prompt: () => 'Why does an upward velocity on its own fail to prove the vehicle is accelerating upward?',
        options: [
          { id: 'a', text: 'Acceleration is the rate of change of velocity. Velocity stays positive while shrinking.' },
          { id: 'b', text: 'Velocity and acceleration always point opposite ways.', feedback: 'Not always. When thrust is above weight and the vehicle starts climbing, both are upward.' },
          { id: 'c', text: 'The velocity plot is less accurate than the acceleration plot.', feedback: 'Both plots are accurate. They measure different things.' },
        ],
        correct: () => 'a',
        hint: 'Think of a ball thrown upward.',
        explanation: 'Velocity tells you where the vehicle is heading. Acceleration tells you how heading is changing.',
        review: 'm0-forces',
      },
      {
        id: 'g0-q4',
        version: 1,
        kind: 'match',
        binds: [],
        prompt: () => 'Match each quantity with its unit.',
        items: [
          { id: 'T', text: 'thrust {{T}}' },
          { id: 'a', text: 'acceleration {{a}}' },
          { id: 'm', text: 'mass {{m}}' },
          { id: 'z', text: 'altitude {{z}}' },
          { id: 'v', text: 'velocity {{v}}' },
        ],
        choices: [
          { id: 'm', text: 'm' },
          { id: 'mps', text: 'm/s' },
          { id: 'mps2', text: 'm/s²' },
          { id: 'N', text: 'N' },
          { id: 'kg', text: 'kg' },
        ],
        correct: () => ({ z: 'm', v: 'mps', a: 'mps2', T: 'N', m: 'kg' }),
        hint: 'Acceleration is metres per second, per second.',
        explanation: 'Keeping units attached is the fastest way to catch mistakes.',
      },
    ],
  },
};
